import { queryOptions } from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';

function getApiBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getAuthHeaders(token: AccessToken): Record<string, string> {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export type PartnershipItem = {
  partnershipId?: string | null;
  donorId?: string | null;
  donorScheduleId?: string | null;
  type?: number | null;
  partnershipBillingType?: number | null;
  account1Id?: string | null;
  account2Id?: string | null;
  account3Id?: string | null;
  account4Id?: string | null;
  percent1?: number | null;
  percent2?: number | null;
  percent3?: number | null;
  percent4?: number | null;
  oddEven1?: number | null;
  oddEven2?: number | null;
  oddEven3?: number | null;
  oddEven4?: number | null;
  viewOnline1?: boolean | null;
  viewOnline2?: boolean | null;
  viewOnline3?: boolean | null;
  viewOnline4?: boolean | null;
};

async function fetchPartnershipsV2(
  token: AccessToken,
  {
    donorId,
    donorScheduleId,
  }: {
    donorId: string;
    donorScheduleId?: string;
  },
): Promise<PartnershipItem[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorId', donorId);
  if (donorScheduleId) {
    searchParams.set('donorScheduleId', donorScheduleId);
  }

  const url = `${getApiBaseUrl()}/api/v2/partnerships?${searchParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as PartnershipItem[];
}

export async function upsertPartnershipV2(
  token: AccessToken,
  input: PartnershipItem,
): Promise<void> {
  const url = `${getApiBaseUrl()}/api/v2/partnerships`;

  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: getAuthHeaders(token),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
}

export const partnershipsOptions = (
  token: AccessToken,
  {
    donorId,
    donorScheduleId,
  }: {
    donorId: string;
    donorScheduleId?: string;
  },
) =>
  queryOptions({
    queryKey: ['partnerships', 'list', { donorId, donorScheduleId }],
    enabled: !!token,
    async queryFn() {
      return fetchPartnershipsV2(token, { donorId, donorScheduleId });
    },
  });
